import React, { useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash-es";

import Text from "../../shared/text/Text";
import RadioButton from "../../shared/RadioButton";
import ZIcon from "../../shared/ZIcon/ZIcon";
import styles from "./ClaimOwnershipModal.module.scss";
import { filterOption } from "../../../utils/helpers";
import MessageTitle from "./MessageTile";

const SelectableListBody = ({
  body,
  state,
  setState,
  onItemSelect,
  onSearch,
}) => {
  const {
    searchConfig,
    messageConfig,
    items = [],
    selectionType = "single",
    emptyConfig,
    fieldId = "id",
  } = body ?? {};
  const {
    imageUrl,
    title: emptyTitle,
    subtitle: emptySubtitle,
  } = emptyConfig ?? {};
  const [query, setQuery] = useState("");
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return undefined;
    }
    if (!query.trim() || !onSearch) return undefined;

    const timer = setTimeout(onSearch, 500);
    return () => clearTimeout(timer);
  }, [onSearch, query]);

  const filteredItems = useMemo(
    () => items.filter((item) => filterOption(query, item, searchConfig)),
    [items, searchConfig, query],
  );

  const selectedId = state?.[fieldId];

  const handleSelect = (id, trackingParams) => {
    if (selectionType !== "single") return;
    if (id !== selectedId) onItemSelect?.(trackingParams);
    setState((s) => ({ ...s, [fieldId]: id }));
  };

  return (
    <div className={styles.selectableBody}>
      {Object.keys(messageConfig).length > 0 && (
        <MessageTitle messageConfig={messageConfig} />
      )}
      {searchConfig ? (
        <div className={styles.selectableSearch}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchConfig.placeholder}
          />
        </div>
      ) : null}

      <div className={styles.selectableList}>
        {filteredItems.map((item) => {
          const { id, icon, primary, secondary, trackingParams } = item ?? {};
          const {
            title: primaryTitle,
            subtitle: primarySubtitle,
            icon: primaryIcon,
          } = primary ?? {};
          const {
            title: secondaryTitle,
            subtitle: secondarySubtitle,
            icon: secondaryIcon,
          } = secondary ?? {};
          const isSelected = id === selectedId;

          return (
            <div
              key={id}
              className={styles.selectableRow}
              onClick={() => handleSelect(id, trackingParams)}
            >
              <section>
                <div className={styles.selectableRowWrapper}>
                  {primaryIcon && (
                    <ZIcon
                      unicode={primaryIcon}
                      className={styles.selectableRowIcon}
                    />
                  )}
                  <div className={styles.selectableRowText}>
                    <Text
                      uiData={primaryTitle}
                      className={styles.selectableRowTitle}
                    />
                    <Text
                      uiData={primarySubtitle}
                      className={styles.selectableRowSubtitle}
                    />
                  </div>
                </div>

                {secondaryTitle && (
                  <div className={styles.selectableRowWrapper}>
                    {secondaryIcon && (
                      <ZIcon
                        unicode={secondaryIcon}
                        className={styles.selectableRowIcon}
                      />
                    )}
                    <div className={styles.selectableRowText}>
                      <Text
                        uiData={secondaryTitle}
                        className={styles.selectableRowTitle}
                      />
                      <Text
                        uiData={secondarySubtitle}
                        className={styles.selectableRowSubtitle}
                      />
                    </div>
                  </div>
                )}
              </section>
              {selectionType === "single" ? (
                <RadioButton checked={isSelected} size={20} color={"red"} />
              ) : null}
            </div>
          );
        })}

        {isEmpty(filteredItems) && (
          <div className={styles.selectableEmpty}>
            {imageUrl && (
              <img src={imageUrl} className={styles.selectableEmptyIcon} />
            )}
            <div>
              {emptyTitle && (
                <Text
                  uiData={emptyTitle}
                  className={styles.selectableEmptyTitle}
                />
              )}
              {emptySubtitle && (
                <Text
                  uiData={emptySubtitle}
                  className={styles.selectableEmptyDescription}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectableListBody;
